import { MutableRefObject, useCallback } from 'react';
import Handsontable from 'handsontable';
import useRefCallback from '../../../utils/react/useRefCallback';
import { useDispatch } from 'react-redux';
import { startEditingElement } from '../../selection/selectionActions';
import { getTableCellEditorId, getTableEditorId, getTableGridEditorId } from '../utils/tableCellEditingUtils';
import {
    TABLE_CELL_EDITOR_KEY,
    TABLE_GRID_EDITOR_KEY,
    TABLE_TITLE_EDITOR_KEY,
} from '../../../../common/table/tableConstants';

/**
 * Hooks used by the Milanote table editing plugins (MilanoteEditingPlugin & MilanoteCellEditor).
 *
 * NOTE: `useStableCallback` allows us to pass these functions into the HotTable config (which is only
 * initialised once) without worrying about the function identity changing on each render.
 */
export const useTableEditingHandlers = ({
    hotTableInstanceRef,
    showTitle,
    elementId,
    isCurrentlyEditingTableCell,
    setCurrentlyEditingCellEditorCoords,
}: {
    hotTableInstanceRef: MutableRefObject<Handsontable | null>;
    showTitle: boolean;
    elementId: string;
    isCurrentlyEditingTableCell: boolean;
    setCurrentlyEditingCellEditorCoords: (cellCoords: { row: number; col: number } | null) => void;
}) => {
    const dispatch = useDispatch();

    const dispatchStartEditingTitle = useCallback(() => dispatch(
            startEditingElement({
                id: elementId,
                editorKey: TABLE_TITLE_EDITOR_KEY,
                editorId: getTableEditorId(elementId),
                elementId: undefined,
            }),
        ), [elementId]);

    // FIXME-TABLES - I don't think this will be working now that we've changed the redux -> hot instance
    //  syncing. We'll need to instead manually set the selected cell, or default it to the first cell.
    const dispatchStartEditingTableCell = useCallback((editorFocusRect) => dispatch(
            startEditingElement({
                id: elementId,
                editorKey: TABLE_CELL_EDITOR_KEY,
                editorId: getTableCellEditorId(elementId),
                editorFocusRect,
                elementId: undefined,
            }),
        ), [elementId]);

    const dispatchStartEditingTableGrid = useCallback(() => dispatch(
            startEditingElement({
                id: elementId,
                editorKey: TABLE_GRID_EDITOR_KEY,
                editorId: getTableGridEditorId(elementId),
                elementId: undefined,
            }),
        ), [elementId]);

    /**
     * Edits the table title.
     */
    const jumpToTitle = useRefCallback(() => {
        showTitle && requestAnimationFrame(() => dispatchStartEditingTitle());
    });

    /**
     * Starts editing the cell editor.
     */
    const startEditingCellEditor = useRefCallback((cellCoords) => {
        setCurrentlyEditingCellEditorCoords(cellCoords);

        requestAnimationFrame(() => {
            if (!isCurrentlyEditingTableCell) {
                // TODO: Once table editor is updated to TipTap, we can use the specific caret client coord
                //       instead of the cell client coord
                const cellRect = hotTableInstanceRef.current
                    ?.getCell(cellCoords.row, cellCoords.col)
                    ?.getBoundingClientRect();

                dispatchStartEditingTableCell(cellRect);
            }
        });
    });

    /**
     * Finishes editing the cell editor.
     */
    const finishEditingCellEditor = useRefCallback(() => {
        setCurrentlyEditingCellEditorCoords(null);

        requestAnimationFrame(() => {
            if (isCurrentlyEditingTableCell) dispatchStartEditingTableGrid();
        });
    });

    return {
        jumpToTitle,
        startEditingCellEditor,
        finishEditingCellEditor,
    };
};
